import React, { useEffect, useMemo, useState } from 'react'
import { GreenTick, Languages } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import './language.css'

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#026D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const RadioCheck = ({ checked }) => (
    checked
        ? <span className='langRadio langRadio--checked'><img src={GreenTick} alt="" /></span>
        : <span className='langRadio' />
)

const getLanguageLabel = (lang) => lang?.name || lang?.title || lang?.code || '—'
const getLanguageSubLabel = (lang) => lang?.native_name || lang?.native || lang?.code || ''

const Language = ({ onBack }) => {
    const [languages, setLanguages] = useState([])
    const [countries, setCountries] = useState([])
    const [selectedLanguageId, setSelectedLanguageId] = useState('')
    const [selectedCountryId, setSelectedCountryId] = useState('')
    const [initialLanguageId, setInitialLanguageId] = useState('')
    const [initialCountryId, setInitialCountryId] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        let mounted = true

        Promise.all([
            service.get(endpoints.users.languages, {}, { silent: true }),
            service.get(endpoints.users.countries, {}, { silent: true }),
            service.get(endpoints.users.userAccount, {}, { silent: true }),
        ])
            .then(([languagesRes, countriesRes, accountRes]) => {
                if (!mounted) return

                const languageList = Array.isArray(languagesRes?.data?.data)
                    ? languagesRes.data.data
                    : []
                const countryList = Array.isArray(countriesRes?.data?.data)
                    ? countriesRes.data.data
                    : []
                const account = accountRes?.data?.data || {}

                const languageId = account.language?.id ?? account.language_id ?? ''
                const countryId = account.country?.id ?? account.country_id ?? ''

                setLanguages(languageList)
                setCountries(countryList)
                setSelectedLanguageId(languageId === '' || languageId == null ? '' : String(languageId))
                setSelectedCountryId(countryId === '' || countryId == null ? '' : String(countryId))
                setInitialLanguageId(languageId === '' || languageId == null ? '' : String(languageId))
                setInitialCountryId(countryId === '' || countryId == null ? '' : String(countryId))
            })
            .catch(() => {
                if (!mounted) return
                setLanguages([])
                setCountries([])
            })
            .finally(() => {
                if (mounted) setLoading(false)
            })

        return () => {
            mounted = false
        }
    }, [])

    const selectedLanguage = useMemo(
        () => languages.find((item) => String(item.id) === String(selectedLanguageId)),
        [languages, selectedLanguageId]
    )

    const selectedCountry = useMemo(
        () => countries.find((item) => String(item.id) === String(selectedCountryId)),
        [countries, selectedCountryId]
    )

    const currentLabel = [
        selectedLanguage ? getLanguageLabel(selectedLanguage) : null,
        selectedCountry?.name || null,
    ].filter(Boolean).join(' • ') || 'Not set'

    const handleApply = async () => {
        if (!selectedLanguageId) {
            showToast('Please select a language', 'warning')
            return
        }

        const payload = new FormData()
        let hasChanges = false

        if (String(selectedLanguageId) !== String(initialLanguageId)) {
            payload.append('language_id', String(selectedLanguageId))
            hasChanges = true
        }
        if (selectedCountryId && String(selectedCountryId) !== String(initialCountryId)) {
            payload.append('country_id', String(selectedCountryId))
            hasChanges = true
        }

        if (!hasChanges) {
            showToast('No changes to update', 'warning')
            return
        }

        setSaving(true)
        try {
            const { data } = await service.put(endpoints.users.updateUserProfile, payload, { silent: true })
            const account = data?.data || {}
            const nextLanguageId = account.language?.id ?? selectedLanguageId
            const nextCountryId = account.country?.id ?? selectedCountryId
            setSelectedLanguageId(String(nextLanguageId))
            setSelectedCountryId(nextCountryId === '' || nextCountryId == null ? '' : String(nextCountryId))
            setInitialLanguageId(String(nextLanguageId))
            setInitialCountryId(nextCountryId === '' || nextCountryId == null ? '' : String(nextCountryId))
            showToast(data?.message || 'Language updated successfully', 'success')
        } catch {
            // handled by service interceptor
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className='languageRegion'>
            <div className='tripDocHeader' onClick={onBack}>
                <BackArrow />
                <span className='bsSectionTitle'>Language &amp; Region</span>
            </div>

            <div className='bsSectionTitle mt-3'>App Preferences</div>
            <div className='bgWhite commonBorder borderRadius20 paddingCommon20 langCurrentBox'>
                <span className='securityMenuItem__icon'>
                    <img src={Languages} alt="" />
                </span>
                <div>
                    <div className='langCurrentBox__label'>Current Settings</div>
                    <div className='langCurrentBox__value'>{loading ? 'Loading...' : currentLabel}</div>
                </div>
            </div>

            <div className='langGrid'>
                <div>
                    <div className='bsSectionTitle'>App Language</div>
                    <div className='bgWhite commonBorder borderRadius20'>
                        {loading ? (
                            <div className='securityMenuItem'>
                                <div className='securityMenuItem__info'>
                                    <div className='securityMenuItem__subtitle'>Loading languages...</div>
                                </div>
                            </div>
                        ) : languages.length ? (
                            languages.map((lang) => (
                                <div
                                    className='securityMenuItem'
                                    key={lang.id}
                                    onClick={() => setSelectedLanguageId(String(lang.id))}
                                >
                                    <div className='securityMenuItem__info'>
                                        <div className='securityMenuItem__title'>{getLanguageLabel(lang)}</div>
                                        {getLanguageSubLabel(lang) ? (
                                            <div className='securityMenuItem__subtitle'>{getLanguageSubLabel(lang)}</div>
                                        ) : null}
                                    </div>
                                    <RadioCheck checked={String(selectedLanguageId) === String(lang.id)} />
                                </div>
                            ))
                        ) : (
                            <div className='securityMenuItem'>
                                <div className='securityMenuItem__info'>
                                    <div className='securityMenuItem__subtitle'>No languages available.</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <div className='bsSectionTitle'>Region</div>
                    <div className='bgWhite commonBorder borderRadius20'>
                        {loading ? (
                            <div className='securityMenuItem'>
                                <div className='securityMenuItem__info'>
                                    <div className='securityMenuItem__subtitle'>Loading regions...</div>
                                </div>
                            </div>
                        ) : countries.length ? (
                            countries.map((country) => (
                                <div
                                    className='securityMenuItem'
                                    key={country.id}
                                    onClick={() => setSelectedCountryId(String(country.id))}
                                >
                                    <div className='securityMenuItem__info'>
                                        <div className='securityMenuItem__title'>{country.name}</div>
                                    </div>
                                    <RadioCheck checked={String(selectedCountryId) === String(country.id)} />
                                </div>
                            ))
                        ) : (
                            <div className='securityMenuItem'>
                                <div className='securityMenuItem__info'>
                                    <div className='securityMenuItem__subtitle'>No regions available.</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className='bsSectionTitle mt-4'>Note</div>
            <div className='bsBox'>
                <p className='bookingConfirmSentText'>
                    Some features may not be available in all languages. Language preference is saved to your profile.
                </p>
            </div>

            <button
                type='button'
                className='commonBtn w-100 mt-4'
                onClick={handleApply}
                disabled={loading || saving}
            >
                {saving ? 'Saving...' : 'Apply Changes'}
            </button>
        </div>
    )
}

export default Language
